import { DOCUMENT } from '@angular/common';
import { Directive, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ICallableObject } from '@df/api/request/request.service';
import { CustomformComponentDef } from '@df/catalog-ui/customform/customform.component.def';
import { ICustomformField } from '@df/catalog/customform/customform';
import { Status } from '@df/core/status';
import { IFileInputEvent } from '@df/ui/form/file-input.component.def';
import each from 'lodash-es/each';
import { CampaignTrackingService } from '../../module/campaign-tracking/campaign-tracking.service';
import { ConsultantsService } from '../../module/consultants/consultants.service';
import { FileInputComponent } from '../../ui/form/file-input/file-input.component';
import { GoogleRecaptcha } from '@df/module-google-recaptcha/google-recaptcha.namespace';

const PREFILLED_MAP = [
    { label: 'gclid', key: 'gclid' },
    { label: 'MSCLKID', key: 'msclkid' },
    { label: 'FBCLID', key: 'fbclid' },
    { label: 'GA Client ID', key: 'gaclid' },
    // { label: 'Traffic Source', key: 'gclid' },
    { label: 'Source', key: 'utm_source' },
    { label: 'Medium', key: 'utm_medium' },
    { label: 'Term', key: 'utm_term' },
    { label: 'Content', key: 'utm_content' },
    { label: 'Campaign', key: 'utm_campaign' }
];

declare module '@df/catalog/customform/customform' {
    interface ICustomformField {
        field_id?: string;
        is_hidden_field?: boolean;
    }

    interface ICustomformData {
        show_captcha?: 1 | 0;
    }
}
declare module '@tomandco/nexus-api-types' {
    namespace Nexus.Lib.General {
        interface AppData {
            customform?: {
                captcha?: {
                    key?: string;
                };
            };
        }
    }
}

@Directive()
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export abstract class AbstractLocalCustomformComponentDef extends CustomformComponentDef<{}> {
    protected localCheckboxesData: { [key: string]: { [key: string]: boolean | undefined } } = {};

    protected dropdownFieldsOtherData: { [key: string]: string } = {};

    protected checkboxesFieldsOtherData: { [key: string]: string } = {};

    private fileStatuses: { [key: string]: Status } = {};

    private fileInputComponents: { [key: string]: FileInputComponent } = {};

    protected checkboxesOtherOptionLabel = '_other_option_label';

    private readonly supportedFields = [
        'text',
        'dropdown',
        'address',
        'email',
        'phone',
        'file_upload',
        'subscription',
        'number',
        'paragraph',
        'checkboxes',
        'radio'
    ];

    protected captchaData: GoogleRecaptcha.IData = {};

    private readonly campaignTrackingService = inject(CampaignTrackingService);

    private readonly consultantsService = inject(ConsultantsService);

    private readonly activatedRoute = inject(ActivatedRoute);

    protected readonly document = inject(DOCUMENT);

    override ngOnInit(): void {
        super.ngOnInit();
        this.subscriptions.add(this.campaignTrackingService.trackingUpdatedSubject.subscribe(() => this.updatePrefiledFields(this.data)));
    }

    protected get showCaptcha(): boolean {
        return !!this.model?.data?.show_captcha;
    }

    protected captchaSubmit = () => {
        this.data['g-recaptcha-response'] = this.captchaData.token;
        this.submit();
    };

    protected override async loadForm() {
        try {
            await this.consultantsService.loadConsultants();
        } catch (error) {}
        super.loadForm();
    }

    protected isSupportedField(fieldType: string): boolean {
        return this.supportedFields.indexOf(fieldType) > -1;
    }

    protected getMaxSize(field: ICustomformField): string {
        const maxSize = field.image_maximum_size || 5;
        return maxSize + 'MB';
    }

    protected onUpload(event: IFileInputEvent, field: ICustomformField): void {
        this.data[field.cid] = event.file;
        const status = this.getFileInputStatus(field);
        if (event.error?.message) {
            status.error = event.error.message;
        } else {
            status.reset();
        }

        this.fileInputComponents[field.cid] = event.component;

        this.detectChanges();
    }

    protected getFileInputStatus(field: ICustomformField): Status {
        if (!this.fileStatuses[field.cid]) {
            const status = new Status();
            this.subscriptions.add(status.subscribe(() => this.detectChanges()));
            this.fileStatuses[field.cid] = status;
        }
        return this.fileStatuses[field.cid];
    }

    protected resetFile(field: ICustomformField): void {
        this.data[field.cid] = undefined;
        this.fileInputComponents[field.cid]?.reset();
        this.getFileInputStatus(field)?.reset();
        this.detectChanges();
    }

    override dataReset(): void {
        super.dataReset();
        this.captchaData = {};
        each(this.fileInputComponents, value => {
            value?.reset();
        });
        each(this.fileStatuses, value => {
            value?.reset();
        });
        if (this.fields) {
            this.fields.forEach(field => {
                if (field.field_type === 'checkboxes') {
                    this.localCheckboxesData[field.cid] = {};
                    const fieldData = this.data[field.cid];
                    if (Array.isArray(fieldData)) {
                        fieldData.forEach(i => {
                            this.localCheckboxesData[field.cid][i] = true;
                        });
                    }
                    this.checkboxesFieldsOtherData[field.cid] = '';
                } else if (field.field_type === 'dropdown') {
                    this.dropdownFieldsOtherData[field.cid] = '';
                }

                const defaultValue = field['default_value'];
                if (defaultValue !== undefined) {
                    switch (field.field_type) {
                        case 'paragraph':
                        case 'text':
                            this.data[field.cid] = String(defaultValue);
                            break;
                        case 'number':
                            this.data[field.cid] = Number(defaultValue);
                            break;
                    }
                }
            });
        }
        this.updatePrefiledFields(this.data);
        this.detectChanges();
    }

    protected localOnCheckboxesChange(field: ICustomformField): void {
        const data = this.localCheckboxesData[field.cid];
        if (data && field?.field_options?.options?.length) {
            const labels = Object.keys(data).filter(key => data[key] === true);
            const selectedOptionsLabels = field.field_options.options
                .filter(option => {
                    return labels.find(label => {
                        return label == option.label;
                    });
                })
                .map(i => i.label);
            if (labels.indexOf(this.checkboxesOtherOptionLabel) > -1) {
                selectedOptionsLabels.push(this.checkboxesOtherOptionLabel);
            }
            this.data[field.cid] = selectedOptionsLabels;
            this.onCheckboxesChange(field);
        }
    }

    protected isOtherForDropdownSelected(field: ICustomformField): boolean {
        if (field.field_type === 'dropdown') {
            const currentValue = this.data?.[field.cid];
            if (typeof currentValue === 'string' && currentValue.toLowerCase() === 'other') {
                return true;
            }
        }
        return false;
    }

    protected override getSubmitAction(): ICallableObject<Partial<{}>> {
        const action = super.getSubmitAction();

        if (action?.data && this.model?.data?.fields) {
            this.model.data.fields.forEach(field => {
                if (field.field_type === 'dropdown') {
                    if (this.isOtherForDropdownSelected(field) && action.data) {
                        action.data[field.cid] = this.dropdownFieldsOtherData[field.cid];
                    }
                } else if (field.field_type === 'checkboxes') {
                    const data = action?.data?.[field.cid];
                    if (data && Array.isArray(data)) {
                        const otherIndex = data.indexOf(this.checkboxesOtherOptionLabel);
                        if (otherIndex > -1) {
                            data[otherIndex] = this.checkboxesFieldsOtherData[field.cid];
                        }
                    }
                }
            });
            this.updatePrefiledFields(action.data);
        }

        return action;
    }

    protected getFieldGridColumn(field: ICustomformField): number {
        if (this.isSM && !this.isNameField(field)) {
            return 12;
        }
        return field.wrappercol || 12;
    }

    private isNameField(field: ICustomformField): boolean {
        const name = field.label?.trim().toLowerCase();
        if (name) {
            return name === 'first name' || name === 'last name';
        }
        return false;
    }

    protected get isSM(): boolean {
        return this.app.client.sizeId < 2;
    }

    private updatePrefiledFields(data: { [key: string]: any }): void {
        if (data) {
            const prefiledData = this.getPrefiledData();
            each(prefiledData, (value, key) => {
                data[key] = value;
            });
            this.detectChanges();
        }
    }

    private getPrefiledData() {
        const data: { [key: string]: any } = {};
        const currentUrl = this.getFieldByLabel('Current URL');
        if (currentUrl) {
            data[currentUrl.cid] = this.currentUrl;
        }
        const referer = this.getFieldByLabel('Referrer url');
        if (referer) {
            data[referer.cid] = this.document.referrer;
        }
        const trafficSource = this.getFieldByLabel('Traffic Source');
        if (trafficSource) {
            data[trafficSource.cid] = this.campaignTrackingService.referrer;
        }
        const tracking = this.campaignTrackingService.getTracking();
        PREFILLED_MAP.forEach(item => {
            const field = this.getFieldByLabel(item.label);
            if (field) {
                const value = tracking[item.key];
                if (value !== undefined) {
                    data[field.cid] = value;
                }
            }
        });

        const queryParams = this.activatedRoute.snapshot.queryParams;
        each(queryParams, (value, key) => {
            const field = this.getFieldById(key);
            if (field) {
                data[field.cid] = value;
            }
        });
        return data;
    }

    protected getFieldByLabel(label: string): ICustomformField | undefined {
        return this.model?.data?.fields?.find(field => field.label?.trim().toLowerCase() === label.trim().toLowerCase());
    }

    protected getFieldById(id: string): ICustomformField | undefined {
        return this.model?.data?.fields?.find(field => field.field_id?.toLowerCase() === id.toLowerCase());
    }

    private get currentUrl(): string {
        const path = this.router.url.split('?')[0].split('#')[0];
        return this.app.url.toUrl(path);
    }

    protected override onModelChange(): void {
        super.onModelChange();

        if (this.fields) {
            this.fields = [...this.fields];
        }
        const consultants = this.consultantsService.getConsultants();
        if (consultants?.length) {
            this.fields?.forEach((field, index) => {
                if (this.isConsultantField(field) && this.fields) {
                    this.fields[index] = {
                        ...field,
                        field_type: 'dropdown',
                        field_options: {
                            options: consultants.map(i => {
                                return {
                                    label: i.label,
                                    checked: false
                                };
                            }),
                            include_blank_option: field.field_options?.include_blank_option || !field.required
                        }
                    };
                }
            });
        }

        this.reset();
        this.detectChanges();
    }

    private isConsultantField(field: ICustomformField): boolean {
        const type = field.field_type;
        if (type === 'dropdown' || type === 'text') {
            return field.field_id?.toLowerCase() === 'as_salesperson' || field.label?.trim().toLowerCase() === 'select sales team member';
        }
        return false;
    }
}
